/**
 * The single HTTP client for the whole app.
 *
 * Three things happen here that must not be re-implemented anywhere else:
 *
 *  1. **Token storage.** The access token lives in a module-scope variable — never
 *     in localStorage or sessionStorage. An XSS on any page can read web storage,
 *     and a stolen long-lived bearer token is game over. A JS variable dies with
 *     the tab, and the *refresh* token is an httpOnly cookie the browser attaches
 *     to `/api/auth/*` but JS can never read. The cost of that choice is that a
 *     page reload has no token, which is exactly why AuthProvider calls
 *     `POST /api/auth/refresh` on mount to mint a fresh one.
 *
 *  2. **Branch scoping.** The API isolates the two Noida branches with an
 *     `x-branch-id` header (see the API's branchScope middleware). Setting it once
 *     here means no caller can forget it and accidentally read the other gym.
 *
 *  3. **Transparent refresh on 401**, deduplicated — see refreshAccessToken().
 */

const BASE_URL = '/api';

// --- Token + branch state ---------------------------------------------------

let accessToken: string | null = null;

/**
 * Bumped every time a *new* access token is installed. A request captures the
 * generation it was sent under; if it comes back 401 and the generation has since
 * moved on, another request already refreshed and this one simply retries with the
 * new token instead of triggering a second, pointless refresh.
 */
let tokenGeneration = 0;

let activeBranchId: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
  tokenGeneration += 1;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setActiveBranchId(branchId: string | null): void {
  activeBranchId = branchId;
}

export function getActiveBranchId(): string | null {
  return activeBranchId;
}

/**
 * Called when the session is unrecoverable (refresh rejected). AuthProvider
 * registers a handler that clears React state; the route guards then redirect.
 * We deliberately do *not* call location.assign() here — a hard navigation would
 * throw away in-flight React state and make the login redirect untestable.
 */
type AuthFailureHandler = () => void;
let onAuthFailure: AuthFailureHandler = () => {};

export function setAuthFailureHandler(handler: AuthFailureHandler): void {
  onAuthFailure = handler;
}

// --- Errors -----------------------------------------------------------------

/** Field-level errors, as produced by the API's Zod error mapper. */
export interface ApiFieldError {
  field: string;
  message: string;
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(message: string, status: number, code: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Narrowed accessor for react-hook-form: `err.fieldErrors` -> setError() calls. */
  get fieldErrors(): ApiFieldError[] {
    if (!Array.isArray(this.details)) return [];
    return this.details.filter(
      (d): d is ApiFieldError =>
        typeof d === 'object' &&
        d !== null &&
        typeof (d as ApiFieldError).field === 'string' &&
        typeof (d as ApiFieldError).message === 'string',
    );
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** Network failure / server unreachable, as opposed to a real HTTP response. */
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

// --- Refresh, deduplicated --------------------------------------------------

interface AuthPayloadLike {
  accessToken: string;
}

let refreshPromise: Promise<string | null> | null = null;

/**
 * Refresh the access token, collapsing concurrent callers onto one request.
 *
 * The dashboard fires seven queries at once. When the token expires they all 401
 * within a few milliseconds of each other. Without deduplication that is seven
 * `POST /auth/refresh` calls, and because the API rotates the refresh token on
 * every use, six of them would present an already-rotated token and be rejected —
 * logging the user out during a perfectly ordinary token expiry.
 *
 * So: the first caller creates the promise and stores it; everyone else awaits the
 * same promise. The slot is cleared only after it settles, so a *later* expiry
 * still gets a fresh attempt.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(`${BASE_URL}/auth/refresh`, {
          method: 'POST',
          // The refresh token is an httpOnly cookie — it only rides along if we
          // explicitly opt in to sending credentials.
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return null;
        const data = (await res.json()) as AuthPayloadLike;
        if (!data?.accessToken) return null;
        setAccessToken(data.accessToken);
        return data.accessToken;
      } catch {
        // Network error during refresh: treat as unauthenticated rather than
        // hanging every queued request forever.
        return null;
      } finally {
        // Cleared last, after all awaiters have attached to this promise.
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

// --- Core request -----------------------------------------------------------

export type QueryValue = string | number | boolean | null | undefined | Array<string | number>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** JSON body. Use `formData` for uploads. */
  body?: unknown;
  formData?: FormData;
  query?: Record<string, QueryValue>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Skip the branch header — for endpoints that are explicitly cross-branch. */
  omitBranch?: boolean;
  /** Skip the 401 -> refresh -> retry dance (used by the auth endpoints themselves). */
  skipAuthRefresh?: boolean;
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = path.startsWith('http') ? path : `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const v of value) params.append(key, String(v));
    } else {
      params.append(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${url}${url.includes('?') ? '&' : '?'}${qs}` : url;
}

/** Built fresh per attempt — a RequestInit with a consumed body cannot be re-sent. */
function buildInit(opts: RequestOptions, accept: string): RequestInit {
  const headers: Record<string, string> = { Accept: accept, ...opts.headers };

  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (!opts.omitBranch && activeBranchId) headers['x-branch-id'] = activeBranchId;

  let body: BodyInit | undefined;
  if (opts.formData) {
    body = opts.formData; // let the browser set the multipart boundary
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  return {
    method: opts.method ?? 'GET',
    headers,
    body,
    signal: opts.signal,
    // Always send cookies: the refresh cookie is scoped to /api/auth, and future
    // same-site session needs (CSRF token) come for free.
    credentials: 'include',
  };
}

async function toApiError(res: Response): Promise<ApiError> {
  let code = `HTTP_${res.status}`;
  let message = res.statusText || 'Request failed';
  let details: unknown;

  try {
    const text = await res.text();
    if (text) {
      const parsed = JSON.parse(text) as Partial<ApiErrorBody>;
      if (parsed?.error) {
        code = parsed.error.code ?? code;
        message = parsed.error.message ?? message;
        details = parsed.error.details;
      }
    }
  } catch {
    // Non-JSON error body (proxy HTML, gateway timeout page) — keep the defaults.
  }

  if (res.status === 502 || res.status === 503 || res.status === 504) {
    message = 'The server is not responding. Please try again in a moment.';
  }

  return new ApiError(message, res.status, code, details);
}

/**
 * Performs the request, transparently refreshing once on a 401.
 * Returns the raw Response so both JSON and blob callers can share the logic.
 */
async function requestRaw(path: string, opts: RequestOptions, accept: string): Promise<Response> {
  const url = buildUrl(path, opts.query);
  const generationAtSend = tokenGeneration;

  let res: Response;
  try {
    res = await fetch(url, buildInit(opts, accept));
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError('Cannot reach the server. Check your connection.', 0, 'NETWORK_ERROR');
  }

  if (res.status !== 401 || opts.skipAuthRefresh) return res;

  // 401 handling — exactly one refresh attempt per request.
  let token: string | null;
  if (tokenGeneration !== generationAtSend) {
    // Someone else already refreshed while this request was in flight. Just retry.
    token = accessToken;
  } else {
    token = await refreshAccessToken();
  }

  if (!token) {
    onAuthFailure();
    throw new ApiError('Your session has expired. Please sign in again.', 401, 'SESSION_EXPIRED');
  }

  try {
    res = await fetch(url, buildInit(opts, accept));
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError('Cannot reach the server. Check your connection.', 0, 'NETWORK_ERROR');
  }

  // Still 401 after a valid refresh: the account lost access (deactivated,
  // branch unassigned). Nothing left to retry.
  if (res.status === 401) {
    onAuthFailure();
    throw await toApiError(res);
  }

  return res;
}

/** Typed JSON fetch. Throws {@link ApiError} for any non-2xx response. */
export async function apiFetch<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await requestRaw(path, opts, 'application/json');
  if (!res.ok) throw await toApiError(res);

  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    return (await res.text()) as unknown as T;
  }
  return (await res.json()) as T;
}

/** Filename from `Content-Disposition`, falling back to the caller's suggestion. */
function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || fallback;
}

/**
 * Downloads a CSV/PDF export through the same authenticated path.
 *
 * A plain `<a href>` or `window.open` cannot carry the Authorization header or the
 * branch header, so exports must go through fetch and an object URL.
 */
export async function download(
  path: string,
  fallbackFilename: string,
  opts: RequestOptions = {},
): Promise<void> {
  const res = await requestRaw(path, opts, '*/*');
  if (!res.ok) throw await toApiError(res);

  const blob = await res.blob();
  const filename = filenameFromDisposition(res.headers.get('content-disposition'), fallbackFilename);

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke on the next tick — revoking synchronously can cancel the download in
  // Safari before it has read the blob.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
}

/** Opens a blob (typically an invoice PDF) in a new tab instead of saving it. */
export async function openBlob(path: string, opts: RequestOptions = {}): Promise<void> {
  const res = await requestRaw(path, opts, '*/*');
  if (!res.ok) throw await toApiError(res);
  const objectUrl = URL.createObjectURL(await res.blob());
  window.open(objectUrl, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

type BodyOpts = Omit<RequestOptions, 'method' | 'body'>;

export const api = {
  get: <T = unknown>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'GET' }),

  post: <T = unknown>(path: string, body?: unknown, opts?: BodyOpts) =>
    apiFetch<T>(path, { ...opts, method: 'POST', body }),

  patch: <T = unknown>(path: string, body?: unknown, opts?: BodyOpts) =>
    apiFetch<T>(path, { ...opts, method: 'PATCH', body }),

  put: <T = unknown>(path: string, body?: unknown, opts?: BodyOpts) =>
    apiFetch<T>(path, { ...opts, method: 'PUT', body }),

  del: <T = unknown>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'DELETE' }),

  upload: <T = unknown>(path: string, formData: FormData, opts?: BodyOpts) =>
    apiFetch<T>(path, { ...opts, method: 'POST', formData }),

  download,
  openBlob,
};

/** Shape shared by every paginated list endpoint. */
/**
 * Mirrors `paginate()` in `packages/api/src/lib/http.ts`.
 *
 * The key is `limit`, not `pageSize` — it has to match what the server actually
 * emits, or every consumer reads `undefined` and silently computes the wrong page
 * count. `<Pagination>` takes a `pageSize` prop; pass `meta.limit` into it.
 */
export interface Paginated<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}
